/* eslint-disable max-len */
import { AnimationEvent } from '@angular/animations';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import moment from 'moment-timezone';

import { ActionKeys } from '../../../../Common/Forms/enums/keys.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { dateFormats } from '../../../../Core/Users/data/date-formats';
import { DatePickerOverlayComponent } from '../DatePickerOverlay/date-picker-overlay.component';
import { DatePickerPanelComponent } from '../DatePickerPanel/date-picker-panel.component';
import { DatePickerService } from '../../services/date-picker.service';
import { CalendarViewsEnum } from '../../enums/date-picker.enum';

/**
 * ### Accessibility Usage
 * + ***ariaLabelledBy*** - use this to associate an external label with the autocomplete. Use id of the label as this value
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 * + ***showOnFocus*** - determine whether the calendar panel should open up when the input is focused. Default is true
 * + ***tabindex*** - use this to set if the date field is focusable
 */
@Component({
    selector: 'agpr-date-picker',
    templateUrl: './date-picker.component.html',
    styleUrls: ['./date-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatePickerComponent),
            multi: true
        },
        DatePickerService // shared with the child panel + overlay
    ],
    standalone: false
})
export class DatePickerComponent implements OnInit, ControlValueAccessor {

    @Input() id: string;
    @Input() name: string;
    @Input() placeholder: string;
    @Input() tabindex: number;
    @Input() ariaLabelledBy: string;
    @Input() panelStyleClass: string;

    @Input() required: boolean;
    @Input() disabled: boolean;
    @Input() readonlyInput: boolean;
    @Input() showIcon: boolean = true;
    @Input() showOnFocus: boolean = true;
    @Input() hideOnDateSelect: boolean = true;
    @Input() hideFormatHint: boolean = false;

    @Input() dataType: string = 'date';
    @Input() selectionMode: string = 'single';
    @Input() dateFormat: string = 'MM/DD/YYYY';
    @Input() shortYearCutoff: any = '+10';
    @Input() icon: string = 'fa-duotone fa-regular fa-calendar-range';

    // Passed straight through to the panel
    @Input() view: CalendarViewsEnum = CalendarViewsEnum.DATE;
    @Input() defaultDate: Date;
    @Input() minDate: Date;
    @Input() maxDate: Date;
    @Input() disabledDates: Date[];
    @Input() disabledDays: number[];

    @Output() focus = new EventEmitter<any>();
    @Output() blur = new EventEmitter<any>();
    @Output() show = new EventEmitter<any>();
    @Output() close = new EventEmitter<any>();
    @Output() select = new EventEmitter<any>();
    @Output() input = new EventEmitter<any>();
    @Output() monthChange = new EventEmitter<any>();
    @Output() yearChange = new EventEmitter<any>();
    @Output() clickOutside = new EventEmitter<any>();

    @ViewChild('container', { static: false }) containerViewChild: ElementRef<HTMLElement>;
    @ViewChild('inputField', { static: false }) inputFieldViewChild: ElementRef<HTMLInputElement>;
    @ViewChild('panel', { static: true }) panel: DatePickerPanelComponent;
    @ViewChild('overlay', { static: true }) overlay: DatePickerOverlayComponent;

    value: any;
    inputFieldValue: string = null;
    inputHasFocus: boolean = false;
    formatHint: string;
    iconAriaLabel: string;


    /** Set when we close the panel and move focus back to the input, so that focus doesn't reopen it. */
    private suppressOpenOnFocus = false;

    readonly validDateFormats: string[] = dateFormats.map(format => format.momentFormat);

    constructor(
        public el: ElementRef,
        public cd: ChangeDetectorRef,
        public localizationService: LocalizationService,
        public datePickerService: DatePickerService
    ) {}

    get overlayVisible(): boolean {
        return !!this.overlay?.visible;
    }

    ngOnInit(): void {
        this.formatHint = this.datePickerService.getFormatHint(this.selectionMode !== 'single');
        this.updateIconAriaLabel();
    }

    writeValue(value: any): void {
        this.value = value;
        if (this.value && typeof this.value === 'string') {
            try {
                this.value = this.datePickerService.parseValueFromString(this.value, this.selectionMode, this.validDateFormats);
            } catch {
                this.value = null;
            }
        }
        this.updateInputField();
        this.updateIconAriaLabel();
    }

    onModelChange: Function = () => {};
    onModelTouched: Function = () => {};

    registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    showPanel(): void {
        if (this.overlayVisible) {
            return;
        }
        this.panel.updatePanelView();
        this.cd.detectChanges(); // make sure the panel is rendered before the overlay measures it
        this.overlay.open();
    }

    hidePanel(event?: any): void {
        if (event && this.datePickerService.isFalseClickOutside(event.target)) {
            return;
        }
        this.overlay.close();
    }

    togglePanel(): void {
        if (!this.overlayVisible && !this.disabled) {
            this.showPanel();
        } else {
            this.hidePanel();
            this.el.nativeElement.querySelector('.agpr-datePicker-toggleButton')?.focus();
        }
    }


    /** Escape inside the panel, or a single date was picked. */
    closePanelAndFocusInput(): void {
        this.hidePanel();
        this.suppressOpenOnFocus = true;
        this.inputFieldViewChild.nativeElement.focus();
    }

    onOverlayOpened(event: AnimationEvent): void {
        this.panel.focusInitial();
        this.show.emit(event);
    }

    onOverlayClosed(event: AnimationEvent): void {
        this.panel.resetView();
        this.close.emit(event);
    }


    onPanelValueChange(value: any): void {
        this.updateModel(value);
        this.updateInputField();
    }

    onPanelDateSelect(date: Date): void {
        this.select.emit(date);
        if (this.selectionMode === 'single' && this.hideOnDateSelect) {
            requestAnimationFrame(() => this.closePanelAndFocusInput());
        }
    }


    onUserInput(event: Event): void {
        const target = event.target as HTMLInputElement;
        const value = this.datePickerService.parseValueFromString(target.value, this.selectionMode, this.validDateFormats);
        if (value && this.datePickerService.isValidSelection(value, this.minDate, this.maxDate, this.disabledDates, this.disabledDays, this.selectionMode)) {
            this.updateModel(value);
            this.panel.navigateTo(value);
        } else {
            // invalid / partial input sets the model to today.
            this.updateModel(new Date());
        }
        this.input.emit(event);
    }

    onInputFocus(event: Event): void {
        this.inputHasFocus = true;
        if (this.showOnFocus && !this.suppressOpenOnFocus) {
            this.showPanel();
        }
        this.suppressOpenOnFocus = false;
        this.focus.emit(event);
    }

    onInputKeydown(event: KeyboardEvent): void {
        switch (event.key) {
            case ActionKeys.DOWN:
                this.showPanel();
                break;
            case ActionKeys.ENTER:
            case ActionKeys.TAB:
            case ActionKeys.ESCAPE:
                if (!event.shiftKey || (event.shiftKey && !this.showIcon)) {
                    this.hidePanel();
                }
                break;
        }
    }

    onInputBlur(event: Event): void {
        this.inputHasFocus = false;
        this.suppressOpenOnFocus = false;
        this.updateInputField();
        this.onModelTouched();
        this.blur.emit(event);
    }

    handleKeyup(event: KeyboardEvent): void {
        event.stopPropagation();
    }

    updateInputField(): void {
        let formattedValue = '';
        if (this.value) {
            if (this.selectionMode === 'single') {
                formattedValue = this.datePickerService.formatDate(this.value, this.validDateFormats);
            } else if (this.selectionMode === 'range' && this.value.length) {
                const [startDate, endDate] = this.value;
                formattedValue = this.datePickerService.formatDate(startDate, this.validDateFormats);
                if (endDate) {
                    formattedValue += ' - ' + this.datePickerService.formatDate(endDate, this.validDateFormats);
                }
            }
        }
        this.inputFieldValue = formattedValue;
        if (this.inputFieldViewChild?.nativeElement) {
            this.inputFieldViewChild.nativeElement.value = this.inputFieldValue;
        }
    }


    updateModel(value: any): void {
        this.value = value;
        if (this.dataType === 'date') {
            this.onModelChange(this.value);
        } else if (this.dataType === 'string') {
            if (this.selectionMode === 'single') {
                this.onModelChange(this.datePickerService.formatDate(this.value, this.validDateFormats));
            } else {
                const stringArrValue = this.value ?
                    this.value.map((date: Date) => this.datePickerService.formatDate(date, this.validDateFormats)) :
                    null;
                this.onModelChange(stringArrValue);
            }
        }
        this.updateIconAriaLabel();
    }

    private updateIconAriaLabel(): void {
        const firstDate = Array.isArray(this.value) ? this.value[0] : this.value;
        this.iconAriaLabel = this.localizationService.transform('Common.datePicker.openCalendar') +
            (firstDate ? ', ' + moment(firstDate).format('MMMM DD, YYYY') : '');
    }
}
